import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    CheckSquare, Square, Tag, DollarSign, Package,
    ArrowUp, ArrowDown, ArrowUpDown, GripVertical, Copy, Check, ExternalLink, AlertTriangle,
    MoreVertical, XCircle, RefreshCw, Settings, Eye, EyeOff, Printer, X, Lock, Unlock,
    CheckCircle2, HelpCircle,
} from "lucide-react";
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import {
    getItemDetails, updateCatalogItemNotes, updateCatalogItemMeta,
    setItemCosts, removeItemCosts, assignItemFlags, removeItemFlags,
    setItemFields, removeItemFields,
    updateItemPrice, updateSfInventory, saveItemFees, retireCatalogItem,
} from "../../services/catalogService";
import { getWalmartInventoryBasic } from "../../services/wfsShipmentService";
import ReplensModal from "./ReplensModal";

const money = (v) => (v === null || v === undefined || v === "" ? "—" : `$${parseFloat(v).toFixed(2)}`);

// Walmart returns these as millisecond epoch strings (e.g. "1789862399000")
const formatEpochMs = (val) => {
    if (!val) return "—";
    const ms = parseInt(val, 10);
    if (Number.isNaN(ms)) return val;
    return new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// Snaps a referral fee's actual %-of-price to whichever of Walmart's
// common referral fee tiers (8 / 10 / 15%) it's closest to — for display
// purposes only, next to the dollar amount in the fee breakdown tooltip.
const REFERRAL_FEE_TIERS = [8, 10, 15];
const snapReferralFeePercent = (feeAmount, price) => {
    if (!feeAmount || !price) return null;
    const actualPct = (parseFloat(feeAmount) / parseFloat(price)) * 100;
    return REFERRAL_FEE_TIERS.reduce((closest, tier) =>
        Math.abs(tier - actualPct) < Math.abs(closest - actualPct) ? tier : closest
    );
};

// ── Label printing (same thermal-label layout as the Batches page) ──
const generateBarcodeDataUrl = (value) => {
    try {
        const canvas = document.createElement("canvas");
        const cleaned = String(value).replace(/\D/g, "");
        let format = "CODE128";
        if (cleaned.length === 12) format = "UPC";
        else if (cleaned.length === 13) format = "EAN13";
        JsBarcode(canvas, cleaned, {
            format, displayValue: false, margin: 2, width: 4, height: 100,
            background: "#ffffff", lineColor: "#000000", flat: true,
        });
        return canvas.toDataURL("image/png");
    } catch {
        return null;
    }
};

const formatExpiryForLabel = (val) => {
    if (!val) return "";
    const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[2]}-${match[3]}-${match[1]}`;
    return val;
};

// Prints one thermal label per unit of quantity for every item passed in —
// used for both the single-row "Label" button and bulk printing of a
// selection. Items with no UPC/GTIN are skipped; returns how many printed.
const printCatalogLabels = (items) => {
    const printable = items.filter((it) => it.gtin);
    if (printable.length === 0) {
        alert("No barcode (UPC/GTIN) available for the selected item(s).");
        return 0;
    }
    const lw = 57.15, lh = 31.75; // mm
    const doc = new jsPDF({ unit: "mm", format: [lh, lw], orientation: "landscape" });
    const pad = 2, inner = lw - pad * 2;
    let firstPage = true;

    printable.forEach((item) => {
        const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
        const barcodeDataUrl = generateBarcodeDataUrl(item.gtin);
        for (let i = 0; i < qty; i++) {
            if (!firstPage) doc.addPage([lh, lw], "landscape");
            firstPage = false;
            doc.setFillColor(255, 255, 255);
            doc.rect(0, 0, lw, lh, "F");
            const barcodeH = lh * 0.45, barcodeY = pad;
            if (barcodeDataUrl) doc.addImage(barcodeDataUrl, "PNG", 3, barcodeY, lw - 6, barcodeH);
            const gtinY = barcodeY + barcodeH + 2.5;
            doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(0, 0, 0);
            doc.text(item.gtin, lw / 2, gtinY, { align: "center" });
            const titleY = gtinY + 3.5;
            doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(30, 30, 30);
            const titleLines = doc.splitTextToSize(item.title || "", inner);
            doc.text(titleLines.slice(0, 2), lw / 2, titleY, { align: "center" });
            if (item.expiration) {
                doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
                doc.text(`EXPIRY DATE ${formatExpiryForLabel(item.expiration)}`, lw / 2, lh - pad - 1, { align: "center" });
            }
        }
    });
    doc.save(`catalog-labels-${Date.now()}.pdf`);
    return printable.length;
};

const COLUMN_ORDER_KEY = "msp_catalog_column_order";
const COLUMN_VISIBILITY_KEY = "msp_catalog_hidden_columns";
const COLUMN_WIDTHS_KEY = "msp_catalog_column_widths";
const LOCKED_COLUMNS_KEY = "msp_catalog_locked_columns";
// Default pinned columns — "product" bundles the image + title together.
const DEFAULT_LOCKED_COLUMNS = ["product"];
// Bump the suffix (_v2, _v3...) any time you need to force this migration
// to run again for everyone — e.g. if you pin another column in the future.
const COST_PRICE_MIGRATION_KEY = "msp_catalog_costprice_pinned_v1";

// Approximate current auto-sized widths (px) — used as the default the
// FIRST time a column is resized, so dragging starts from roughly where
// it already visually sits today rather than jumping to a generic size.
const DEFAULT_COLUMN_WIDTH = 130;
const DEFAULT_COLUMN_WIDTHS_BY_ID = {
    actions: 80, sku_report: 110, print_label: 140, product: 260, sku: 240, upc_gtin: 120, price: 130,
    sf_stock: 110, wfs_stock: 130, supplier: 140, expiration: 130, purchase_date: 130, date_added: 160,
    total_fees: 110, profit_roi: 110, price_incentive: 140, flags: 140, status: 120, notes: 220,
};

const loadStoredWidths = () => {
    try {
        const raw = localStorage.getItem(COLUMN_WIDTHS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

// Human-readable labels for the generic fee_breakdown jsonb keys — new fee
// types can be added here without touching the save/read logic at all.
const FEE_LABELS = {
    referral_fee: "Referral Fee",
    wfs_fee: "WFS Fee",
    fulfillment_cost: "Fulfillment Cost",
    inbound_shipping: "Inbound Shipping",
    sales_tax: "Sales Tax (on cost)",
};

// Dims & weight from the live lookup, cached per item for 4 days so the
// same item is never fetched twice within that window.
const DIMS_CACHE_KEY = "msp_item_dims_cache_v1";
const DIMS_CACHE_TTL_MS = 4 * 24 * 60 * 60 * 1000;

const loadDimsCache = () => {
    try {
        const raw = localStorage.getItem(DIMS_CACHE_KEY);
        const all = raw ? JSON.parse(raw) : {};
        const now = Date.now();
        const fresh = {};
        Object.entries(all).forEach(([id, v]) => {
            if (v && v.savedAt && now - v.savedAt < DIMS_CACHE_TTL_MS) fresh[id] = v;
        });
        return fresh;
    } catch {
        return {};
    }
};

const saveDimsToCache = (itemId, entry) => {
    try {
        const all = loadDimsCache(); // also drops anything older than 4 days
        all[itemId] = entry;
        localStorage.setItem(DIMS_CACHE_KEY, JSON.stringify(all));
    } catch { /* non-fatal */ }
};

// Build the full column list (static + one per cost definition).
// Each column: id, label, sortable, numeric (optional), getValue(item, ctx) -> comparable value.
const buildColumns = (costDefinitions, fieldDefinitions = []) => {
    // "Cost Price" is special: it's just another cost_definitions row under
    // the hood, but conceptually it belongs right next to Sell Price, not
    // lumped in alphabetically with every other custom cost at the end.
    const costPriceDef = costDefinitions.find((c) => c.name?.toLowerCase() === "cost price");
    const otherCostDefs = costDefinitions.filter((c) => c.id !== costPriceDef?.id);

    return [
        { id: "product", label: "Product", sortable: true, sortKey: "title" },
        { id: "sku", label: "SKU", sortable: true, sortKey: "sku" },
        { id: "upc_gtin", label: "UPC/GTIN", sortable: false },
        { id: "price", label: "Sell Price", sortable: true, sortKey: "price" },
        ...(costPriceDef ? [{
            id: `cost:${costPriceDef.id}`,
            label: "Cost Price",
            sortable: true,
            sortKey: "cost_price",
            tooltip: "Buy Cost — the cost of the product (per unit)",
        }] : []),
        { id: "price_incentive", label: "Price Incentive", sortable: false },
        { id: "sf_stock", label: "SF Stock", sortable: true, sortKey: "sf_stock" },
        { id: "wfs_stock", label: "WFS Stock", sortable: true, sortKey: "wfs_stock" },
        { id: "supplier", label: "Supplier", sortable: true, sortKey: "supplier" },
        { id: "expiration", label: "Expiration", sortable: true, sortKey: "expiration_date" },
        { id: "purchase_date", label: "Purchase Date", sortable: true, sortKey: "purchase_date" },
        { id: "total_fees", label: "Total Fees", sortable: true, sortKey: "total_fees" },
        { id: "profit_roi", label: "Profit / ROI", sortable: false },
        ...otherCostDefs.map((c) => ({
            id: `cost:${c.id}`,
            label: c.name,
            sortable: false, // cost values live in a joined table — not sortable server-side
        })),
        ...fieldDefinitions.map((f) => ({
            id: `field:${f.id}`,
            label: f.name,
            sortable: false, // free-text values live in a joined table — not sortable server-side
        })),
        { id: "flags", label: "Flags", sortable: false },
        { id: "status", label: "Status", sortable: true, sortKey: "published_status" },
        { id: "notes", label: "Notes", sortable: false },
        { id: "date_added", label: "Added to MSP", sortable: true, sortKey: "created_at" },
        { id: "sku_report", label: "SKU Report", sortable: false },
        { id: "print_label", label: "Print Label", sortable: false },
        { id: "actions", label: "Actions", sortable: false },
    ];
};

const loadStoredOrder = () => {
    try {
        const raw = localStorage.getItem(COLUMN_ORDER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const CatalogTable = ({ items, costDefinitions, flagDefinitions, fieldDefinitions = [], selectedIds, toggleSelect, toggleSelectAll, onEditCosts, onEditFlags, onEditFields, onEditPrice, onEditInventory, sortBy, sortDir, onSortToggle, autoScrollEnabled, onOpenSkuReport, incentivesBySku = {} }) => {
    const allSelected = items.length > 0 && items.every((i) => selectedIds.includes(i.id));

    // ── Edge auto-scroll (horizontal) — toggle itself lives in CatalogFilters,
    // this component just consumes the current enabled/disabled state. ──
    const scrollContainerRef = useRef(null);
    const autoScrollFrameRef = useRef(null);
    const autoScrollDirectionRef = useRef(0); // -1 left, 0 none, 1 right

    const EDGE_ZONE_PX = 60; // how close to the edge triggers scrolling
    const MAX_SCROLL_SPEED = 8; // px per frame at the very edge (reduced from 14 for a gentler feel)
    const DWELL_MS = 600; // how long you must stay in the edge zone before it starts

    const edgeEnteredAtRef = useRef(null); // timestamp when cursor entered an edge zone, or null
    const pendingDirectionRef = useRef(0); // direction/intensity detected, but not yet "armed"

    const isOverInteractiveElement = (target) => {
        if (!target || !target.closest) return false;
        return !!target.closest("input, button, select, textarea, a, [role='button'], [contenteditable='true']");
    };

    const handleTableMouseMove = (e) => {
        if (!autoScrollEnabled || !scrollContainerRef.current) return;

        if (isOverInteractiveElement(e.target)) {
            edgeEnteredAtRef.current = null;
            pendingDirectionRef.current = 0;
            autoScrollDirectionRef.current = 0;
            return;
        }

        const rect = scrollContainerRef.current.getBoundingClientRect();
        const distFromLeft = e.clientX - rect.left;
        const distFromRight = rect.right - e.clientX;

        let detectedDirection = 0;
        if (distFromLeft < EDGE_ZONE_PX) {
            const intensity = 1 - Math.max(distFromLeft, 0) / EDGE_ZONE_PX;
            detectedDirection = -Math.ceil(intensity * MAX_SCROLL_SPEED);
        } else if (distFromRight < EDGE_ZONE_PX) {
            const intensity = 1 - Math.max(distFromRight, 0) / EDGE_ZONE_PX;
            detectedDirection = Math.ceil(intensity * MAX_SCROLL_SPEED);
        }

        if (detectedDirection === 0) {
            // Left the edge zone entirely — reset the dwell timer and stop scrolling.
            edgeEnteredAtRef.current = null;
            pendingDirectionRef.current = 0;
            autoScrollDirectionRef.current = 0;
            return;
        }

        pendingDirectionRef.current = detectedDirection;
        if (edgeEnteredAtRef.current === null) {
            edgeEnteredAtRef.current = Date.now(); // just entered the edge zone, start the clock
        }
    };

    const handleTableMouseLeave = () => {
        edgeEnteredAtRef.current = null;
        pendingDirectionRef.current = 0;
        autoScrollDirectionRef.current = 0;
    };

    useEffect(() => {
        const step = () => {
            // Only start actually scrolling once the cursor has dwelled in the
            // edge zone for DWELL_MS — a quick pass-through near the edge
            // (e.g. moving to click something else) won't trigger it.
            if (edgeEnteredAtRef.current !== null && Date.now() - edgeEnteredAtRef.current >= DWELL_MS) {
                autoScrollDirectionRef.current = pendingDirectionRef.current;
            }
            const speed = autoScrollDirectionRef.current;
            if (speed !== 0 && scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft += speed;
            }
            autoScrollFrameRef.current = requestAnimationFrame(step);
        };
        autoScrollFrameRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(autoScrollFrameRef.current);
    }, []);

    // ── Image hover preview (fixed-position, escapes table's overflow clipping) ──
    const [hoverPreview, setHoverPreview] = useState(null); // { url, top, left } | null

    // ── Product title hover tooltip (fixed-position, shows the full untruncated title) ──
    const [titleTooltip, setTitleTooltip] = useState(null); // { text, top, left } | null

    // ── Detail popover (click the SF/WFS number to see the full breakdown) ──
    const [openDetailFor, setOpenDetailFor] = useState(null); // `${itemId}:sf` | `${itemId}:wfs` | null

    // ── Copy-to-clipboard for SKU ──
    const [copiedId, setCopiedId] = useState(null);
    const [copiedUpcId, setCopiedUpcId] = useState(null);
    const handleCopySku = (e, item) => {
        e.stopPropagation();
        navigator.clipboard.writeText(item.sku);
        setCopiedId(item.id);
        setTimeout(() => setCopiedId(null), 1200);
    };

    const handleCopyUpc = (e, item) => {
        e.stopPropagation();
        navigator.clipboard.writeText(item.upc || item.gtin || "");
        setCopiedUpcId(item.id);
        setTimeout(() => setCopiedUpcId(null), 1200);
    };
    const detailPopoverRef = useRef(null);

    useEffect(() => {
        if (!openDetailFor) return;
        const handleClickOutside = (e) => {
            if (detailPopoverRef.current && !detailPopoverRef.current.contains(e.target)) {
                setOpenDetailFor(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDetailFor]);

    // ── Details: brand / image / description (per-row, on demand) ──
    const [detailsLoading, setDetailsLoading] = useState({});
    const [detailsById, setDetailsById] = useState({});

    // ── Notes (inline editable, saved on blur) ──
    const [notesDraft, setNotesDraft] = useState({});   // id -> in-progress text (only while editing)
    const [notesSaved, setNotesSaved] = useState({});   // id -> { notes, notes_updated_at } after a successful save
    const [notesSaving, setNotesSaving] = useState({});

    // ── Supplier / Expiration (inline editable, saved on blur, same pattern as notes) ──
    const [metaDraft, setMetaDraft] = useState({});   // `${id}:${field}` -> in-progress text
    const [metaSaving, setMetaSaving] = useState({}); // `${id}:${field}` -> true | { error }

    const metaKey = (id, field) => `${id}:${field}`;
    const getMetaValue = (item, field) => {
        const key = metaKey(item.id, field);
        return metaDraft[key] !== undefined ? metaDraft[key] : (item[field] ?? "");
    };
    const handleMetaChange = (item, field, value) =>
        setMetaDraft((prev) => ({ ...prev, [metaKey(item.id, field)]: value }));

    const handleMetaBlur = async (item, field) => {
        const key = metaKey(item.id, field);
        const draft = metaDraft[key];
        if (draft === undefined) return;
        const previousValue = item[field] ?? "";
        if (draft === previousValue) {
            setMetaDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            return;
        }
        setMetaSaving((prev) => ({ ...prev, [key]: true }));
        try {
            await updateCatalogItemMeta(item.id, { [field]: draft || null });
            item[field] = draft; // reflect immediately without a full refetch
            setMetaDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            setMetaSaving((prev) => { const next = { ...prev }; delete next[key]; return next; });
        } catch (e) {
            setMetaSaving((prev) => ({ ...prev, [key]: { error: e.message || "Failed to save" } }));
        }
    };

    const getNotesValue = (item) =>
        notesDraft[item.id] !== undefined ? notesDraft[item.id] : (notesSaved[item.id]?.notes ?? item.notes ?? "");

    const getNotesTimestamp = (item) => notesSaved[item.id]?.notes_updated_at ?? item.notes_updated_at;

    const handleNotesChange = (item, value) => setNotesDraft((prev) => ({ ...prev, [item.id]: value }));

    const handleNotesBlur = async (item) => {
        const draft = notesDraft[item.id];
        if (draft === undefined) return; // nothing changed, nothing to save
        const previousValue = notesSaved[item.id]?.notes ?? item.notes ?? "";
        if (draft === previousValue) {
            setNotesDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        setNotesSaving((prev) => ({ ...prev, [item.id]: true }));
        try {
            const res = await updateCatalogItemNotes(item.id, draft);
            setNotesSaved((prev) => ({ ...prev, [item.id]: res }));
            setNotesDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        } catch (e) {
            setNotesSaving((prev) => ({ ...prev, [item.id]: { error: e.message || "Failed to save" } }));
            return;
        } finally {
            setNotesSaving((prev) => {
                const next = { ...prev };
                if (next[item.id] !== undefined && typeof next[item.id] !== "object") delete next[item.id];
                return next;
            });
        }
    };

    const formatNotesTimestamp = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    // ── Inline custom-field editing (per row, per field definition) — same
    // shape as inline cost editing, just a plain string, no numeric parsing ──
    const [fieldDraft, setFieldDraft] = useState({});
    const [fieldSaved, setFieldSaved] = useState({});
    const [fieldSaving, setFieldSaving] = useState({});

    const fieldKey = (itemId, defId) => `${itemId}:${defId}`;

    const getFieldValue = (item, defId) => {
        const key = fieldKey(item.id, defId);
        if (fieldDraft[key] !== undefined) return fieldDraft[key];
        if (fieldSaved[key] !== undefined) return fieldSaved[key] ?? "";
        const found = (item.fields || []).find((f) => f.field_definition_id === defId);
        return found ? found.value : "";
    };

    const handleFieldChange = (item, defId, value) =>
        setFieldDraft((prev) => ({ ...prev, [fieldKey(item.id, defId)]: value }));

    const handleFieldBlur = async (item, defId) => {
        const key = fieldKey(item.id, defId);
        const draft = fieldDraft[key];
        if (draft === undefined) return;
        const original = fieldSaved[key] !== undefined
            ? (fieldSaved[key] ?? "")
            : ((item.fields || []).find((f) => f.field_definition_id === defId)?.value ?? "");
        if (draft === original) {
            setFieldDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            return;
        }
        setFieldSaving((prev) => ({ ...prev, [key]: true }));
        try {
            if (draft.trim() === "") {
                await removeItemFields([item.id], defId);
                setFieldSaved((prev) => ({ ...prev, [key]: null }));
            } else {
                await setItemFields([item.id], defId, draft.trim());
                setFieldSaved((prev) => ({ ...prev, [key]: draft.trim() }));
            }
            setFieldDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            setFieldSaving((prev) => { const next = { ...prev }; delete next[key]; return next; });
        } catch (e) {
            setFieldSaving((prev) => ({ ...prev, [key]: { error: e.message || "Failed to save" } }));
        }
    };

    // ── Inline cost editing (per row, per cost definition) ──
    const [costDraft, setCostDraft] = useState({});   // `${itemId}:${defId}` -> in-progress text
    const [costSaved, setCostSaved] = useState({});   // `${itemId}:${defId}` -> number | null (after save)
    const [costSaving, setCostSaving] = useState({}); // `${itemId}:${defId}` -> true | { error }

    const costKey = (itemId, defId) => `${itemId}:${defId}`;

    const getCostValue = (item, defId) => {
        const key = costKey(item.id, defId);
        if (costDraft[key] !== undefined) return costDraft[key];
        if (costSaved[key] !== undefined) return costSaved[key] === null ? "" : String(costSaved[key]);
        const found = (item.costs || []).find((c) => c.cost_definition_id === defId);
        return found ? String(found.value) : "";
    };

    const getOriginalCostString = (item, defId) => {
        const key = costKey(item.id, defId);
        if (costSaved[key] !== undefined) return costSaved[key] === null ? "" : String(costSaved[key]);
        const found = (item.costs || []).find((c) => c.cost_definition_id === defId);
        return found ? String(found.value) : "";
    };

    const handleCostChange = (item, defId, value) =>
        setCostDraft((prev) => ({ ...prev, [costKey(item.id, defId)]: value }));

    const commitCost = async (item, defId, valueOrNull) => {
        const key = costKey(item.id, defId);
        setCostSaving((prev) => ({ ...prev, [key]: true }));
        try {
            if (valueOrNull === null) {
                await removeItemCosts([item.id], defId);
                setCostSaved((prev) => ({ ...prev, [key]: null }));
            } else {
                await setItemCosts([item.id], defId, valueOrNull);
                setCostSaved((prev) => ({ ...prev, [key]: valueOrNull }));
            }
            setCostDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            setCostSaving((prev) => { const next = { ...prev }; delete next[key]; return next; });
        } catch (e) {
            setCostSaving((prev) => ({ ...prev, [key]: { error: e.message || "Failed to save" } }));
        }
    };

    // Returns { clear: true } | { clear: false, value } | null (invalid) | undefined (no draft / unchanged)
    const validateCostDraft = (item, defId) => {
        const key = costKey(item.id, defId);
        const draft = costDraft[key];
        if (draft === undefined) return undefined;
        const trimmed = draft.trim();
        const original = getOriginalCostString(item, defId);
        if (trimmed === original) return undefined; // no change, nothing to do
        if (trimmed === "") return { clear: true };
        const numeric = parseFloat(trimmed);
        if (Number.isNaN(numeric)) {
            setCostSaving((prev) => ({ ...prev, [key]: { error: "Enter a number" } }));
            return null;
        }
        return { clear: false, value: numeric };
    };

    // Enter key = explicit confirmation, saves immediately
    const handleCostKeyDown = (item, defId, e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const result = validateCostDraft(item, defId);
        if (result === undefined) {
            const key = costKey(item.id, defId);
            setCostDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            return;
        }
        if (result === null) return;
        commitCost(item, defId, result.clear ? null : result.value);
    };

    // Blur (click/tab away) = save immediately, no confirmation popover.
    // (Cost is DB-only — no Walmart call — so this is safe to fire on blur,
    // unlike Price / SF Stock which touch the Walmart API and keep the confirm step.)
    const handleCostBlur = (item, defId) => {
        const result = validateCostDraft(item, defId);
        if (result === undefined) {
            const key = costKey(item.id, defId);
            setCostDraft((prev) => { const next = { ...prev }; delete next[key]; return next; });
            return;
        }
        if (result === null) return; // invalid input — error already set by validateCostDraft
        commitCost(item, defId, result.clear ? null : result.value);
    };

    // ── Inline price editing ──
    const [priceDraft, setPriceDraft] = useState({});
    const [priceSaving, setPriceSaving] = useState({});
    const [pricePendingConfirm, setPricePendingConfirm] = useState({}); // itemId -> numeric value awaiting OK/Cancel

    const getPriceValue = (item) =>
        priceDraft[item.id] !== undefined
            ? priceDraft[item.id]
            : (item.price === null || item.price === undefined ? "" : String(item.price));

    const handlePriceChange = (item, value) => setPriceDraft((prev) => ({ ...prev, [item.id]: value }));

    const commitPrice = async (item, numeric) => {
        setPricePendingConfirm((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        setPriceSaving((prev) => ({ ...prev, [item.id]: true }));
        try {
            await updateItemPrice(item.id, item.sku, numeric);
            item.price = numeric; // reflect immediately without a full refetch
            setPriceDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            setPriceSaving((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        } catch (e) {
            setPriceSaving((prev) => ({ ...prev, [item.id]: { error: e.message || "Failed to update price" } }));
        }
    };

    const validatePriceDraft = (item) => {
        const draft = priceDraft[item.id];
        if (draft === undefined) return null;
        const trimmed = draft.trim();
        const numeric = parseFloat(trimmed);
        if (trimmed === "" || Number.isNaN(numeric) || numeric < 0) {
            setPriceSaving((prev) => ({ ...prev, [item.id]: { error: "Enter a valid price" } }));
            return null;
        }
        return numeric;
    };

    // Enter key = explicit confirmation, saves immediately
    const handlePriceKeyDown = (item, e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const numeric = validatePriceDraft(item);
        if (numeric === null) return;
        if (numeric === item.price) {
            setPriceDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        commitPrice(item, numeric);
    };

    // Blur (click/tab away) = show inline confirm popover instead of saving directly
    const handlePriceBlur = (item) => {
        const numeric = validatePriceDraft(item);
        if (numeric === null) return;
        if (numeric === item.price) {
            setPriceDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        setPricePendingConfirm((prev) => ({ ...prev, [item.id]: numeric }));
    };

    const cancelPriceConfirm = (item) => {
        setPricePendingConfirm((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        setPriceDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    };

    // ── Buy Box price check (on-demand, per row) — same live scrape used
    // elsewhere for weight/dims, since it also returns the item's current
    // listed price on Walmart.com. Cached in local state so re-rendering
    // (sorting, editing another cell, etc.) never re-fires the lookup —
    // only an explicit click does.
    // ── Dims & weight (on-demand, per row, cached 4 days in localStorage) ──
    const [dimsById, setDimsById] = useState(() => loadDimsCache()); // itemId -> { weight, dimensions, savedAt } | { loading } | { error }

    const loadDims = async (item) => {
        if (!item.wpid) {
            setDimsById((prev) => ({ ...prev, [item.id]: { error: "No Walmart Item ID on file" } }));
            return;
        }
        setDimsById((prev) => ({ ...prev, [item.id]: { loading: true } }));
        try {
            const data = await getWalmartInventoryBasic(item.wpid);
            const entry = { weight: data.weight ?? null, dimensions: data.dimensions || null, savedAt: Date.now() };
            saveDimsToCache(item.id, entry);
            setDimsById((prev) => ({ ...prev, [item.id]: entry }));
        } catch {
            setDimsById((prev) => ({ ...prev, [item.id]: { error: "Lookup failed" } }));
        }
    };

    const [buyBoxById, setBuyBoxById] = useState({}); // itemId -> { price, loading, error }

    const checkBuyBox = async (item) => {
        if (!item.wpid) {
            setBuyBoxById((prev) => ({ ...prev, [item.id]: { error: "No Walmart Item ID on file" } }));
            return;
        }
        setBuyBoxById((prev) => ({ ...prev, [item.id]: { loading: true } }));
        try {
            const data = await getWalmartInventoryBasic(item.wpid);
            setBuyBoxById((prev) => ({ ...prev, [item.id]: { price: data.price ?? null } }));
        } catch (e) {
            setBuyBoxById((prev) => ({ ...prev, [item.id]: { error: "Lookup failed" } }));
        }
    };

    // ── Inline Total Fees editing (manual override — replaces the whole
    // breakdown with a single {manual: X} entry, tagged source: 'manual') ──
    const [feeDraft, setFeeDraft] = useState({});
    const [feeSaving, setFeeSaving] = useState({});
    const [feePendingConfirm, setFeePendingConfirm] = useState({});

    const getFeeDisplayValue = (item) =>
        feeDraft[item.id] !== undefined ? feeDraft[item.id] : (item.total_fees != null ? String(item.total_fees) : "");

    const handleFeeChange = (item, value) => setFeeDraft((prev) => ({ ...prev, [item.id]: value }));

    const commitFee = async (item, numeric) => {
        setFeePendingConfirm((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        setFeeSaving((prev) => ({ ...prev, [item.id]: true }));
        try {
            await saveItemFees(item.id, { manual: numeric }, numeric, "manual");
            item.total_fees = numeric;
            item.fee_breakdown = { manual: numeric };
            item.source = "manual";
            const costBasis = item.total_cost_basis || 0;
            item.estimated_profit = (item.price || 0) - costBasis - numeric;
            item.estimated_roi = costBasis > 0 ? Math.round((item.estimated_profit / costBasis) * 1000) / 10 : null;
            setFeeDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            setFeeSaving((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        } catch (e) {
            setFeeSaving((prev) => ({ ...prev, [item.id]: { error: e.message || "Failed to save" } }));
        }
    };

    const validateFeeDraft = (item) => {
        const draft = feeDraft[item.id];
        if (draft === undefined) return null;
        const trimmed = draft.trim();
        const numeric = parseFloat(trimmed);
        if (trimmed === "" || Number.isNaN(numeric) || numeric < 0) {
            setFeeSaving((prev) => ({ ...prev, [item.id]: { error: "Enter a valid amount" } }));
            return null;
        }
        return numeric;
    };

    const handleFeeKeyDown = (item, e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const numeric = validateFeeDraft(item);
        if (numeric === null) return;
        if (numeric === item.total_fees) {
            setFeeDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        commitFee(item, numeric);
    };

    const handleFeeBlur = (item) => {
        const numeric = validateFeeDraft(item);
        if (numeric === null) return;
        if (numeric === item.total_fees) {
            setFeeDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        setFeePendingConfirm((prev) => ({ ...prev, [item.id]: numeric }));
    };

    const cancelFeeConfirm = (item) => {
        setFeePendingConfirm((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        setFeeDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    };

    // ── Row actions: Retire, Change SKU (informational only) ──
    const [openActionsFor, setOpenActionsFor] = useState(null); // item id, or null
    const [actionsMenuPos, setActionsMenuPos] = useState(null); // {top, left, openUpward} in viewport coords
    const [retireConfirmItem, setRetireConfirmItem] = useState(null);
    const [retiring, setRetiring] = useState(false);
    const [retireError, setRetireError] = useState("");
    const [retireResultItem, setRetireResultItem] = useState(null); // { sku, feedId } | null, shown after a successful retire
    const [feedIdCopied, setFeedIdCopied] = useState(false);

    // ── Bulk retire (selected rows, one at a time) ──
    const [bulkRetireConfirm, setBulkRetireConfirm] = useState(false);
    const [bulkRetiring, setBulkRetiring] = useState(false);
    const [bulkRetireProgress, setBulkRetireProgress] = useState({ done: 0, total: 0 });
    const [bulkRetireResults, setBulkRetireResults] = useState(null); // [{ sku, feedId, error }] | null
    const [changeSkuInfoItem, setChangeSkuInfoItem] = useState(null);
    const [replensModalItem, setReplensModalItem] = useState(null);
    const [incentiveModalItem, setIncentiveModalItem] = useState(null);
    const actionsMenuRef = useRef(null);

    const MENU_HEIGHT_ESTIMATE = 90; // ~2 rows, used only to decide flip direction

    const MENU_WIDTH = 192; // matches w-48

    const toggleActionsMenu = (itemId, buttonEl) => {
        if (openActionsFor === itemId) {
            setOpenActionsFor(null);
            setActionsMenuPos(null);
            return;
        }
        const rect = buttonEl.getBoundingClientRect();
        const openUpward = window.innerHeight - rect.bottom < MENU_HEIGHT_ESTIMATE + 12;
        // Anchor the menu's RIGHT edge to the button's right edge (grows
        // leftward) rather than the button's left edge — the button sits
        // near the right side of its column, so growing rightward from
        // there was pushing the menu off-screen. Clamped to at least 8px
        // from the viewport's left edge as a final safety net.
        const left = Math.max(8, rect.right - MENU_WIDTH);
        setActionsMenuPos({
            left,
            top: rect.top - MENU_HEIGHT_ESTIMATE + 78,
        });
        setOpenActionsFor(itemId);
    };

    useEffect(() => {
        if (!openActionsFor) return;
        const handleClickOutside = (e) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
                setOpenActionsFor(null);
                setActionsMenuPos(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openActionsFor]);

    const handleRetireConfirmed = async () => {
        if (!retireConfirmItem) return;
        setRetiring(true);
        setRetireError("");
        try {
            const result = await retireCatalogItem(retireConfirmItem.id);
            retireConfirmItem.lifecycle_status = "Retired";
            setRetireResultItem({ sku: retireConfirmItem.sku, feedId: result?.feed_id || null });
            setRetireConfirmItem(null);
        } catch (e) {
            setRetireError(e.message || "Failed to retire item");
        } finally {
            setRetiring(false);
        }
    };

    // Retires every selected, not-already-retired item one at a time (so one
    // failure doesn't abort the rest), collecting a per-item result for the
    // summary modal shown afterward.
    const handleBulkRetireConfirmed = async () => {
        const targets = items.filter((it) => selectedIds.includes(it.id) && it.lifecycle_status !== "Retired");
        setBulkRetireConfirm(false);
        setBulkRetiring(true);
        setBulkRetireProgress({ done: 0, total: targets.length });
        const results = [];
        for (const it of targets) {
            try {
                const result = await retireCatalogItem(it.id);
                it.lifecycle_status = "Retired";
                results.push({ sku: it.sku, feedId: result?.feed_id || null });
            } catch (e) {
                results.push({ sku: it.sku, error: e.message || "Failed to retire item" });
            }
            setBulkRetireProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        }
        setBulkRetiring(false);
        setBulkRetireResults(results);
    };

    // ── Inline SF inventory editing ──
    const [sfDraft, setSfDraft] = useState({});
    const [sfSaving, setSfSaving] = useState({});
    const [sfPendingConfirm, setSfPendingConfirm] = useState({}); // itemId -> numeric value awaiting OK/Cancel

    const handleSfInventoryChange = (item, value) => setSfDraft((prev) => ({ ...prev, [item.id]: value }));

    const currentSfValue = (item) => (item.sf_stock === undefined ? null : item.sf_stock);

    const commitSfInventory = async (item, numeric) => {
        setSfPendingConfirm((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        setSfSaving((prev) => ({ ...prev, [item.id]: true }));
        try {
            const res = await updateSfInventory(item.id, item.sku, numeric);
            const confirmedQty = res?.sf?.quantity?.amount ?? numeric;
            item.sf_stock = confirmedQty; // reflect immediately without a full refetch
            if (item.inventory?.sf) item.inventory.sf.availToSellQty = confirmedQty;
            setSfDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            setSfSaving((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        } catch (e) {
            setSfSaving((prev) => ({ ...prev, [item.id]: { error: e.message || "Failed to update inventory" } }));
        }
    };

    const validateSfDraft = (item) => {
        const draft = sfDraft[item.id];
        if (draft === undefined) return null;
        const trimmed = draft.trim();
        const numeric = parseInt(trimmed, 10);
        if (trimmed === "" || Number.isNaN(numeric) || numeric < 0) {
            setSfSaving((prev) => ({ ...prev, [item.id]: { error: "Enter a valid quantity" } }));
            return null;
        }
        return numeric;
    };

    // Enter key = explicit confirmation, saves immediately
    const handleSfInventoryKeyDown = (item, e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const numeric = validateSfDraft(item);
        if (numeric === null) return;
        if (numeric === currentSfValue(item)) {
            setSfDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        commitSfInventory(item, numeric);
    };

    // Blur (click/tab away) = show inline confirm popover instead of saving directly
    const handleSfInventoryBlur = (item) => {
        const numeric = validateSfDraft(item);
        if (numeric === null) return;
        if (numeric === currentSfValue(item)) {
            setSfDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
            return;
        }
        setSfPendingConfirm((prev) => ({ ...prev, [item.id]: numeric }));
    };

    const cancelSfConfirm = (item) => {
        setSfPendingConfirm((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        setSfDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    };

    // ── Inline flag editing (per row popover) ──
    const [openFlagsFor, setOpenFlagsFor] = useState(null);   // item id with open popover, or null
    const [flagOverride, setFlagOverride] = useState({});     // `${itemId}:${flagId}` -> true/false since last save
    const [flagSaving, setFlagSaving] = useState({});         // `${itemId}:${flagId}` -> true | { error }

    const flagKey = (itemId, flagId) => `${itemId}:${flagId}`;

    const isFlagActive = (item, flagId) => {
        const key = flagKey(item.id, flagId);
        if (flagOverride[key] !== undefined) return flagOverride[key];
        return (item.flags || []).some((f) => f.flag_definition_id === flagId);
    };

    const toggleItemFlag = async (item, flagDef) => {
        const key = flagKey(item.id, flagDef.id);
        const currentlyActive = isFlagActive(item, flagDef.id);
        setFlagSaving((prev) => ({ ...prev, [key]: true }));
        try {
            if (currentlyActive) {
                await removeItemFlags([item.id], flagDef.id);
            } else {
                await assignItemFlags([item.id], flagDef.id);
            }
            setFlagOverride((prev) => ({ ...prev, [key]: !currentlyActive }));
            setFlagSaving((prev) => { const next = { ...prev }; delete next[key]; return next; });
        } catch (e) {
            setFlagSaving((prev) => ({ ...prev, [key]: { error: e.message || "Failed" } }));
        }
    };

    const loadItemDetails = async (item) => {
        setDetailsLoading((prev) => ({ ...prev, [item.id]: true }));
        try {
            const res = await getItemDetails(item.id);
            setDetailsById((prev) => ({ ...prev, [item.id]: res }));
        } catch (e) {
            setDetailsById((prev) => ({ ...prev, [item.id]: { error: e.message || "Failed to load details" } }));
        } finally {
            setDetailsLoading((prev) => ({ ...prev, [item.id]: false }));
        }
    };

    // ── Auto-load brand/image for every visible row, one at a time ──
    const attemptedDetailsRef = useRef(new Set());
    const autoLoadRunningRef = useRef(false);

    useEffect(() => {
        const pending = items.filter(
            (item) =>
                !item.image_url &&
                !detailsById[item.id] &&
                !attemptedDetailsRef.current.has(item.id)
        );
        if (pending.length === 0 || autoLoadRunningRef.current) return;

        autoLoadRunningRef.current = true;
        (async () => {
            for (const item of pending) {
                attemptedDetailsRef.current.add(item.id);
                await loadItemDetails(item);
            }
            autoLoadRunningRef.current = false;
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    // ── Bulk label printing for selected rows ──
    const [showPrintQtyModal, setShowPrintQtyModal] = useState(false);
    const [printQuantities, setPrintQuantities] = useState({}); // itemId -> quantity string
    const [labelQtyDraft, setLabelQtyDraft] = useState({}); // itemId -> quantity string, for the standalone Print Label column

    const openPrintQtyModal = () => {
        const defaults = {};
        items.filter((it) => selectedIds.includes(it.id)).forEach((it) => { defaults[it.id] = "1"; });
        setPrintQuantities(defaults);
        setShowPrintQtyModal(true);
    };

    const handleConfirmBulkPrint = () => {
        const selectedItems = items.filter((it) => selectedIds.includes(it.id));
        const printed = printCatalogLabels(selectedItems.map((it) => ({
            gtin: it.upc || it.gtin,
            title: it.title,
            expiration: it.expiration_date,
            quantity: parseInt(printQuantities[it.id], 10) || 1,
        })));
        if (printed > 0) setShowPrintQtyModal(false);
    };

    // ── Column config + persisted order ──
    // ── Export selected rows to CSV, for use with Batches' "Import from CSV" ──
    const [showExportInstructions, setShowExportInstructions] = useState(false);

    const exportSelectedToCsv = () => {
        const selectedItems = items.filter((it) => selectedIds.includes(it.id));
        if (selectedItems.length === 0) return;
        const headers = ["SKU", "UPC", "GTIN", "Title", "Item ID"];
        const escapeCsv = (val) => {
            if (val == null) return "";
            const str = String(val);
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const rows = selectedItems.map((it) => [it.sku, it.upc, it.gtin, it.title, it.wpid].map(escapeCsv));
        const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `catalog_selected_${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setShowExportInstructions(true);
    };

    const columns = useMemo(() => buildColumns(costDefinitions, fieldDefinitions), [costDefinitions, fieldDefinitions]);
    const columnMap = useMemo(() => Object.fromEntries(columns.map((c) => [c.id, c])), [columns]);

    const [columnOrder, setColumnOrder] = useState(() => {
        const stored = loadStoredOrder();
        const allIds = buildColumns(costDefinitions, fieldDefinitions).map((c) => c.id);
        if (!stored) return allIds;
        // keep stored order, drop ids that no longer exist, append any new ids at the end
        const filtered = stored.filter((id) => allIds.includes(id));
        const missing = allIds.filter((id) => !filtered.includes(id));
        return [...filtered, ...missing];
    });

    // Reconcile order whenever cost definitions change (added/removed/renamed)
    useEffect(() => {
        const allIds = columns.map((c) => c.id);
        setColumnOrder((prev) => {
            const filtered = prev.filter((id) => allIds.includes(id));
            const missing = allIds.filter((id) => !filtered.includes(id));
            const next = [...filtered, ...missing];
            return next.length === prev.length && next.every((v, idx) => v === prev[idx]) ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    // One-time migration: pin "Cost Price" directly after "Sell Price" for
    // anyone with a pre-existing saved column order. Runs once per browser
    // (tracked via COST_PRICE_MIGRATION_KEY) — after that, manual reordering
    // is fully respected again, even if they later drag Cost Price elsewhere.
    useEffect(() => {
        let alreadyMigrated = true;
        try {
            alreadyMigrated = !!localStorage.getItem(COST_PRICE_MIGRATION_KEY);
        } catch {
            return; // no localStorage access — skip silently
        }
        if (alreadyMigrated) return;

        const costPriceDef = costDefinitions.find((c) => c.name?.toLowerCase() === "cost price");
        if (!costPriceDef) return; // not loaded yet / doesn't exist — try again next time costDefinitions changes

        const costPriceId = `cost:${costPriceDef.id}`;

        setColumnOrder((prev) => {
            const priceIdx = prev.indexOf("price");
            if (priceIdx === -1 || !prev.includes(costPriceId)) return prev; // nothing sensible to do
            const alreadyNextToPrice = prev[priceIdx + 1] === costPriceId;
            const next = alreadyNextToPrice
                ? prev
                : (() => {
                    const withoutCostPrice = prev.filter((id) => id !== costPriceId);
                    const newPriceIdx = withoutCostPrice.indexOf("price");
                    const result = [...withoutCostPrice];
                    result.splice(newPriceIdx + 1, 0, costPriceId);
                    return result;
                })();
            return next;
        });

        try {
            localStorage.setItem(COST_PRICE_MIGRATION_KEY, "1");
        } catch {
            // ignore — worst case it retries next mount
        }
    }, [costDefinitions]);

    useEffect(() => {
        localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(columnOrder));
    }, [columnOrder]);

    // ── Hide/show columns — persisted the same way as column order, just a
    // separate key so reordering and visibility don't interfere with each other. ──
    const [hiddenColumns, setHiddenColumns] = useState(() => {
        try {
            const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
            return raw ? new Set(JSON.parse(raw)) : new Set();
        } catch {
            return new Set();
        }
    });
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const columnPickerRef = useRef(null);

    // ── Locked (pinned/sticky) columns — persisted the same way as
    // visibility/order, in their own key. Defaults to pinning the
    // "product" column (image + title) for first-time users. ──
    const [lockedColumns, setLockedColumns] = useState(() => {
        try {
            const raw = localStorage.getItem(LOCKED_COLUMNS_KEY);
            return raw ? new Set(JSON.parse(raw)) : new Set(DEFAULT_LOCKED_COLUMNS);
        } catch {
            return new Set(DEFAULT_LOCKED_COLUMNS);
        }
    });

    useEffect(() => {
        localStorage.setItem(LOCKED_COLUMNS_KEY, JSON.stringify([...lockedColumns]));
    }, [lockedColumns]);

    // Drop any locked ids that no longer exist as columns (e.g. a cost
    // definition was deleted) so a stale id can't silently break the layout.
    useEffect(() => {
        const allIds = new Set(columns.map((c) => c.id));
        setLockedColumns((prev) => {
            const filtered = [...prev].filter((id) => allIds.has(id));
            return filtered.length === prev.size ? prev : new Set(filtered);
        });
    }, [columns]);

    const toggleColumnLock = (id) => setLockedColumns((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // ── Resizable columns, persisted per-user in localStorage ──
    const [columnWidths, setColumnWidths] = useState(() => loadStoredWidths());
    const resizingRef = useRef(null); // { colId, startX, startWidth } | null
    const justResizedRef = useRef(false); // true for one tick after a resize ends, to swallow the stray click it produces

    useEffect(() => {
        localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidths));
    }, [columnWidths]);

    const getColumnWidth = (colId) => columnWidths[colId] ?? DEFAULT_COLUMN_WIDTHS_BY_ID[colId] ?? DEFAULT_COLUMN_WIDTH;

    const startResize = (e, colId) => {
        e.preventDefault();
        e.stopPropagation(); // don't trigger drag-to-reorder or sort click
        resizingRef.current = { colId, startX: e.clientX, startWidth: getColumnWidth(colId) };
        // Without this, dragging across header/cell text starts a native
        // text-selection drag alongside the resize, which is visually
        // distracting and can leave stray selected text after releasing.
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        document.addEventListener("mousemove", handleResizeMove);
        document.addEventListener("mouseup", stopResize);
    };

    const handleResizeMove = (e) => {
        const r = resizingRef.current;
        if (!r) return;
        e.preventDefault();
        const delta = e.clientX - r.startX;
        const next = Math.max(50, r.startWidth + delta); // 50px floor so a column can't be dragged to nothing
        setColumnWidths((prev) => ({ ...prev, [r.colId]: next }));
    };

    const stopResize = () => {
        resizingRef.current = null;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", stopResize);
        // The browser fires a click right after this mouseup, on whatever
        // header cell the mouse happens to be over — often NOT the column
        // being resized, and unrelated to any real sort intent. Swallow
        // exactly one click (via a capture-phase listener so it runs
        // before React's own onClick) so releasing a resize drag never
        // triggers a sort toggle.
        justResizedRef.current = true;
        document.addEventListener("click", suppressNextClick, true);
    };

    const suppressNextClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        justResizedRef.current = false;
        document.removeEventListener("click", suppressNextClick, true);
    };

    const resetColumnWidths = () => {
        setColumnWidths({});
        localStorage.removeItem(COLUMN_WIDTHS_KEY);
    };

    useEffect(() => {
        localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify([...hiddenColumns]));
    }, [hiddenColumns]);

    useEffect(() => {
        if (!showColumnPicker) return;
        const handleClickOutside = (e) => {
            if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
                setShowColumnPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showColumnPicker]);

    const toggleColumnVisibility = (id) => setHiddenColumns((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const visibleOrderedColumns = columnOrder.map((id) => columnMap[id]).filter(Boolean).filter((c) => !hiddenColumns.has(c.id));
    // Locked columns always render first (in their existing relative order),
    // followed by unlocked ones — this keeps pinned columns contiguous at
    // the left edge so their sticky offsets stack cleanly.
    const orderedColumns = [
        ...visibleOrderedColumns.filter((c) => lockedColumns.has(c.id)),
        ...visibleOrderedColumns.filter((c) => !lockedColumns.has(c.id)),
    ];

    // Left offset (px) for each locked column's sticky position — starts
    // after the 44px checkbox column, then stacks each locked column's width.
    const stickyLeftByColId = useMemo(() => {
        const map = {};
        let offset = 44;
        for (const col of orderedColumns) {
            if (!lockedColumns.has(col.id)) break;
            map[col.id] = offset;
            offset += getColumnWidth(col.id);
        }
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderedColumns, lockedColumns, columnWidths]);

    // ── Drag to reorder ──
    const [dragId, setDragId] = useState(null);
    const handleDragStart = (id) => setDragId(id);
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = (targetId) => {
        if (!dragId || dragId === targetId) return;
        setColumnOrder((prev) => {
            const next = prev.filter((id) => id !== dragId);
            const targetIdx = next.indexOf(targetId);
            next.splice(targetIdx, 0, dragId);
            return next;
        });
        setDragId(null);
    };

    // Flags true if any WFS units have been sitting longer than 270 days —
    // based on Walmart's own inventoryAge buckets (271-365d, 365+d).
    const hasAgedWfsStock = (item) => {
        const age = item.inventory?.wfs?.inventoryAge;
        if (!age) return false;
        return (
            // (age['271To365days'] || 0) > 0 ||
            (age['365PlusDays'] || 0) > 0
        );
    };

    // ── Sorting (server-side now — this just reflects state and dispatches
    // the click up to the parent, which refetches page 1 with sort_by/sort_dir) ──
    const sortIcon = (col) => {
        if (!col.sortable) return null;
        if (sortBy !== col.sortKey) return <ArrowUpDown size={12} className="text-gray-300" />;
        return sortDir === "asc" ? <ArrowUp size={12} className="text-teal-600" /> : <ArrowDown size={12} className="text-teal-600" />;
    };

    // ── Cell rendering per column id ──
    const renderCell = (col, item) => {
        if (col.id === "actions") {
            const isOpen = openActionsFor === item.id;
            const isRetired = item.lifecycle_status === "Retired";
            return (
                <td key={col.id} className="px-3 py-2 relative">
                    <button
                        onClick={(e) => toggleActionsMenu(item.id, e.currentTarget)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                        title="Actions"
                    >
                        <MoreVertical size={16} />
                    </button>

                    {isRetired && (
                        <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 align-middle">
                            Retired
                        </span>
                    )}

                    {isOpen && actionsMenuPos && (
                        <div ref={actionsMenuRef}
                            className="fixed z-[999] bg-white border border-gray-200 rounded-xl shadow-2xl py-1.5 w-48"
                            style={{ top: actionsMenuPos.top, left: actionsMenuPos.left }}>
                            <button
                                onClick={() => { setChangeSkuInfoItem(item); setOpenActionsFor(null); setActionsMenuPos(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left"
                            >
                                <RefreshCw size={13} className="text-blue-500" /> Change SKU
                            </button>
                            <button
                                onClick={() => { setRetireConfirmItem(item); setOpenActionsFor(null); setActionsMenuPos(null); }}
                                disabled={isRetired}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 text-left disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                                <XCircle size={13} /> {isRetired ? "Already Retired" : "Retire Item"}
                            </button>
                        </div>
                    )}
                </td>
            );
        }
        if (col.id === "sku_report") {
            return (
                <td key={col.id} className="px-3 py-2">
                    {onOpenSkuReport && (
                        <button
                            onClick={() => onOpenSkuReport(item.id)}
                            className="text-[10px] font-bold text-teal-600 hover:text-teal-700 border border-teal-200 rounded-full px-2 py-1 whitespace-nowrap"
                            title="Open the full sales/velocity report for this item — pulls 7/30/90-day+ figures without slowing down the table"
                        >
                            📊 SKU Report
                        </button>
                    )}
                </td>
            );
        }
        if (col.id === "print_label") {
            const qtyVal = labelQtyDraft[item.id] ?? "1";
            const hasBarcode = !!(item.upc || item.gtin);
            return (
                <td key={col.id} className="px-3 py-2">
                    <div className="flex items-center gap-1">
                        <input
                            type="number" min="1"
                            value={qtyVal}
                            onChange={(e) => setLabelQtyDraft((prev) => ({ ...prev, [item.id]: e.target.value }))}
                            disabled={!hasBarcode}
                            className="w-12 text-xs border border-gray-200 rounded-lg px-1.5 py-1 focus:outline-none focus:border-teal-400 disabled:opacity-40"
                            title={hasBarcode ? "Number of labels to print" : "No barcode on file"}
                        />
                        <button
                            onClick={() => printCatalogLabels([{
                                gtin: item.upc || item.gtin, title: item.title,
                                expiration: item.expiration_date, quantity: parseInt(qtyVal, 10) || 1,
                            }])}
                            disabled={!hasBarcode}
                            className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-700 border border-blue-200 rounded-full px-2 py-1 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                            title={hasBarcode ? "Print thermal label(s) for this item" : "No barcode on file"}
                        >
                            <Printer size={10} /> Print
                        </button>
                    </div>
                </td>
            );
        }
        if (col.id === "product") {
            const imageUrl = detailsById[item.id]?.image_url ?? item.image_url;
            const marketplaceIcon = `/icons/${(item.marketplace || "").toLowerCase()}.png`;
            const walmartUrl = item.marketplace === "walmart" && item.wpid
                ? `https://www.walmart.com/ip/${item.wpid}`
                : null;

            const openListing = () => {
                if (walmartUrl) window.open(walmartUrl, "_blank", "noopener,noreferrer");
            };

            return (
                <td key={col.id} className="px-3 py-2 max-w-[280px]">
                    <div className="flex items-center gap-2">
                        <div className="relative w-12 h-12 flex-shrink-0">
                            {imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt=""
                                    onClick={openListing}
                                    className={`w-12 h-12 rounded object-contain border border-gray-100 ${walmartUrl ? "cursor-pointer" : "cursor-zoom-in"}`}
                                    onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setHoverPreview({ url: imageUrl, top: rect.top, left: rect.right + 10 });
                                    }}
                                    onMouseLeave={() => setHoverPreview(null)}
                                />
                            ) : (
                                <div
                                    onClick={openListing}
                                    className={`w-12 h-12 flex items-center justify-center ${walmartUrl ? "cursor-pointer" : ""}`}
                                >
                                    <Package size={16} className="text-gray-300" />
                                </div>
                            )}
                            {item.marketplace && (
                                <img
                                    src={marketplaceIcon}
                                    alt={item.marketplace}
                                    title={item.marketplace}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full border border-white bg-white shadow-sm object-contain"
                                />
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <span
                                onClick={openListing}
                                onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setTitleTooltip({ text: item.title || "Untitled", top: rect.bottom + 6, left: rect.left });
                                }}
                                onMouseLeave={() => setTitleTooltip(null)}
                                className={`truncate font-medium text-gray-800 flex items-center gap-1 ${walmartUrl ? "cursor-pointer hover:text-teal-600 hover:underline" : ""}`}
                            >
                                {item.title || "Untitled"}
                                {walmartUrl && <ExternalLink size={11} className="text-gray-300 flex-shrink-0" />}
                            </span>
                            <button
                                onClick={() => setReplensModalItem(item)}
                                className="mt-1 text-[10px] font-bold text-teal-600 hover:text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 whitespace-nowrap"
                            >
                                Configure Replens ({item.replen_count || 0})
                            </button>
                        </div>
                    </div>
                </td>
            );
        }
        if (col.id === "brand") {
            const brand = detailsById[item.id]?.brand ?? item.brand;
            const err = detailsById[item.id]?.error;
            return (
                <td key={col.id} className="px-3 py-2">
                    {brand ? (
                        <span className="text-gray-700">{brand}</span>
                    ) : detailsLoading[item.id] ? (
                        <span className="text-xs text-gray-400">Loading...</span>
                    ) : err ? (
                        <span className="text-[10px] text-red-400">—</span>
                    ) : (
                        <span className="text-gray-300">—</span>
                    )}
                </td>
            );
        }
        if (col.id === "sku") {
            return (
                <td key={col.id} className="px-3 py-2 font-mono text-xs text-gray-600">
                    <div className="flex items-center gap-1.5">
                        <span>{item.sku}</span>
                        <button
                            onClick={(e) => handleCopySku(e, item)}
                            title="Copy SKU"
                            className="text-gray-300 hover:text-teal-600 transition"
                        >
                            {copiedId === item.id ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                        </button>
                    </div>
                </td>
            );
        }
        if (col.id === "upc_gtin") {
            const upcGtinValue = item.upc || item.gtin;
            return (
                <td key={col.id} className="px-3 py-2 font-mono text-xs text-gray-600">
                    {upcGtinValue ? (
                        <div className="flex items-center gap-1.5">
                            <span>{upcGtinValue}</span>
                            <button
                                onClick={(e) => handleCopyUpc(e, item)}
                                title={item.upc ? "Copy UPC" : "Copy GTIN"}
                                className="text-gray-300 hover:text-teal-600 transition"
                            >
                                {copiedUpcId === item.id ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                            </button>
                        </div>
                    ) : (
                        <span className="text-gray-300">—</span>
                    )}
                </td>
            );
        }
        if (col.id === "price") {
            const saving = priceSaving[item.id];
            const err = saving && typeof saving === "object" ? saving.error : null;
            const pendingValue = pricePendingConfirm[item.id];
            return (
                <td key={col.id} className="px-3 py-2 text-right relative">
                    <div className="flex items-center justify-end gap-1">
                        <span className="text-gray-400 text-xs">$</span>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={getPriceValue(item)}
                            onChange={(e) => handlePriceChange(item, e.target.value)}
                            onBlur={() => handlePriceBlur(item)}
                            onKeyDown={(e) => handlePriceKeyDown(item, e)}
                            placeholder="—"
                            className="w-20 text-xs text-right border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                        />
                    </div>
                    {saving === true && <div className="text-[10px] text-gray-400 text-right">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500 text-right">{err}</div>}
                    {pendingValue !== undefined && (
                        <div className="absolute z-[9999999] top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2.5 w-40 text-left">
                            <p className="text-[11px] text-gray-600 mb-2">Set price to <span className="font-bold">${pendingValue}</span>?</p>
                            <div className="flex gap-1.5">
                                <button
                                    onClick={() => commitPrice(item, pendingValue)}
                                    className="flex-1 py-1 bg-teal-600 text-white rounded-lg text-[11px] font-bold hover:bg-teal-700 transition"
                                >
                                    OK
                                </button>
                                <button
                                    onClick={() => cancelPriceConfirm(item)}
                                    className="flex-1 py-1 border border-gray-200 text-gray-500 rounded-lg text-[11px] font-semibold hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {(() => {
                        const bb = buyBoxById[item.id];
                        if (!bb) {
                            return (
                                <button onClick={() => checkBuyBox(item)}
                                    className="block ml-auto mt-0.5 text-[10px] text-gray-400 hover:text-teal-600 underline decoration-dotted">
                                    Check BuyBox
                                </button>
                            );
                        }
                        if (bb.loading) return <div className="text-[10px] text-gray-400 text-right mt-0.5">Checking...</div>;
                        if (bb.error) return <div className="text-[10px] text-red-400 text-right mt-0.5">{bb.error}</div>;
                        return (
                            <div className="text-[10px] text-right mt-0.5">
                                <span className="text-gray-500">BuyBox: </span>
                                <span className="font-semibold text-gray-700">{bb.price != null ? `$${bb.price}` : "—"}</span>
                            </div>
                        );
                    })()}
                </td>
            );
        }
        if (col.id === "sf_stock") {
            const savingSf = sfSaving[item.id];
            const sfErr = savingSf && typeof savingSf === "object" ? savingSf.error : null;
            const sfPrevAvail = item.inventory?.sf_previous?.availToSellQty;
            const isOpen = openDetailFor === `${item.id}:sf`;
            const diff = (sfPrevAvail !== undefined && sfPrevAvail !== null && item.sf_stock !== sfPrevAvail)
                ? item.sf_stock - sfPrevAvail : null;

            return (
                <td key={col.id} className="px-3 py-2 relative" style={{ overflow: "visible" }}>
                    <div className="flex items-center gap-1 flex-wrap">
                        <input
                            type="text"
                            inputMode="numeric"
                            value={sfDraft[item.id] !== undefined ? sfDraft[item.id] : String(item.sf_stock ?? "")}
                            onChange={(e) => handleSfInventoryChange(item, e.target.value)}
                            onBlur={() => handleSfInventoryBlur(item)}
                            onKeyDown={(e) => handleSfInventoryKeyDown(item, e)}
                            placeholder="—"
                            className="w-12 text-xs font-semibold border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                        />
                        <button
                            onClick={() => setOpenDetailFor(isOpen ? null : `${item.id}:sf`)}
                            className="text-[10px] text-gray-400 hover:text-teal-600 underline"
                        >
                            details
                        </button>
                        {diff !== null && (
                            <span className={`text-[10px] font-semibold ${diff > 0 ? "text-green-600" : "text-red-500"}`}>
                                ({diff > 0 ? "+" : ""}{diff})
                            </span>
                        )}
                    </div>
                    {savingSf === true && <div className="text-[10px] text-gray-400">Saving...</div>}
                    {sfErr && <div className="text-[10px] text-red-500">{sfErr}</div>}

                    {sfPendingConfirm[item.id] !== undefined && (
                        <div className="absolute z-[9999999] top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2.5 w-44 text-left">
                            <p className="text-[11px] text-gray-600 mb-2">Set SF stock to <span className="font-bold">{sfPendingConfirm[item.id]}</span>?</p>
                            <div className="flex gap-1.5">
                                <button
                                    onClick={() => commitSfInventory(item, sfPendingConfirm[item.id])}
                                    className="flex-1 py-1 bg-teal-600 text-white rounded-lg text-[11px] font-bold hover:bg-teal-700 transition"
                                >
                                    OK
                                </button>
                                <button
                                    onClick={() => cancelSfConfirm(item)}
                                    className="flex-1 py-1 border border-gray-200 text-gray-500 rounded-lg text-[11px] font-semibold hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {isOpen && (
                        <div ref={detailPopoverRef} className="absolute z-[9999999] top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-64 text-left text-xs space-y-1">
                            <div className="font-bold text-gray-700 mb-1">SF Inventory — by ship node</div>
                            {Array.isArray(item.inventory?.sf?.nodes) && item.inventory.sf.nodes.length > 0 ? (
                                item.inventory.sf.nodes.map((n) => (
                                    <div key={n.shipNode} className="border-b border-gray-50 pb-1">
                                        <div className="text-gray-400 text-[10px]">node {n.shipNode}</div>
                                        <div>Available: <span className="font-semibold">{n.availToSellQty?.amount ?? 0}</span></div>
                                        <div>Reserved: {n.reservedQty?.amount ?? 0}</div>
                                        <div>Input (pending): {n.inputQty?.amount ?? 0}</div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-400">No node data yet — run a sync.</div>
                            )}
                            <button onClick={() => setOpenDetailFor(null)} className="text-[10px] text-gray-400 hover:text-gray-600 pt-1">Close</button>
                        </div>
                    )}
                </td>
            );
        }
        if (col.id === "wfs_stock") {
            const wfsData = item.inventory?.wfs || {};
            const wfsPrevAvail = item.inventory?.wfs_previous?.availableUnits;
            const isOpen = openDetailFor === `${item.id}:wfs`;
            const aged = hasAgedWfsStock(item);
            const diff = (wfsPrevAvail !== undefined && wfsPrevAvail !== null && item.wfs_stock !== wfsPrevAvail)
                ? item.wfs_stock - wfsPrevAvail : null;

            return (
                <td key={col.id} className="px-3 py-2 relative" style={{ overflow: "visible" }}>
                    <div className="flex items-center gap-1 flex-wrap text-xs">
                        <span className="font-semibold">{item.wfs_stock ?? "—"}</span>
                        {item.wfs_inbound > 0 && <span className="text-blue-500">(+{item.wfs_inbound} inbound)</span>}
                        {aged && <AlertTriangle size={12} className="text-orange-500" title="Contains stock aged 90+ days" />}
                        <button
                            onClick={() => setOpenDetailFor(isOpen ? null : `${item.id}:wfs`)}
                            className={`text-[10px] underline ${aged ? "text-orange-600 hover:text-orange-700 font-semibold" : "text-gray-400 hover:text-teal-600"}`}
                        >
                            details
                        </button>
                        {diff !== null && (
                            <span className={`text-[10px] font-semibold ${diff > 0 ? "text-green-600" : "text-red-500"}`}>
                                ({diff > 0 ? "+" : ""}{diff})
                            </span>
                        )}
                    </div>

                    {isOpen && (
                        <div ref={detailPopoverRef} className="absolute z-[9999999] top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-64 text-left text-xs space-y-1">
                            <div className="font-bold text-gray-700 mb-1">WFS Inventory — full detail</div>
                            <div>Available: <span className="font-semibold">{wfsData.availableUnits ?? "—"}</span></div>
                            <div>Reserved: {wfsData.reservedUnits ?? "—"}</div>
                            <div>Inbound: {wfsData.inboundUnits ?? "—"}</div>
                            <div>On hand: {wfsData.onhandUnits ?? "—"}</div>
                            <div>Stock status: {wfsData.stockStatus ?? "—"}</div>
                            {wfsData.inventoryAge && (
                                <div className="pt-1 border-t border-gray-50">
                                    <div className="text-gray-400 text-[10px] mb-0.5 flex items-center gap-1">
                                        Age breakdown
                                        {aged && <AlertTriangle size={11} className="text-orange-500" />}
                                    </div>
                                    <div>0-90d: {wfsData.inventoryAge['0To90days'] ?? 0}</div>
                                    <div className={(wfsData.inventoryAge['91To180days'] || 0) > 0 ? "text-orange-600 font-semibold" : ""}>91-180d: {wfsData.inventoryAge['91To180days'] ?? 0}</div>
                                    <div className={(wfsData.inventoryAge['181To270days'] || 0) > 0 ? "text-orange-600 font-semibold" : ""}>181-270d: {wfsData.inventoryAge['181To270days'] ?? 0}</div>
                                    <div className={(wfsData.inventoryAge['271To365days'] || 0) > 0 ? "text-orange-600 font-semibold" : ""}>271-365d: {wfsData.inventoryAge['271To365days'] ?? 0}</div>
                                    <div className={(wfsData.inventoryAge['365PlusDays'] || 0) > 0 ? "text-orange-600 font-semibold" : ""}>365+d: {wfsData.inventoryAge['365PlusDays'] ?? 0}</div>
                                </div>
                            )}
                            {wfsData.firstInStockDate && <div>First in stock: {wfsData.firstInStockDate}</div>}
                            <button onClick={() => setOpenDetailFor(null)} className="text-[10px] text-gray-400 hover:text-gray-600 pt-1">Close</button>
                        </div>
                    )}
                </td>
            );
        }
        if (col.id.startsWith("cost:")) {
            const defId = col.id.slice(5);
            const key = costKey(item.id, defId);
            const saving = costSaving[key];
            const err = saving && typeof saving === "object" ? saving.error : null;
            return (
                <td key={col.id} className="px-3 py-2 text-right relative">
                    <div className="flex items-center justify-end gap-1">
                        <span className="text-gray-400 text-xs">$</span>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={getCostValue(item, defId)}
                            onChange={(e) => handleCostChange(item, defId, e.target.value)}
                            onBlur={() => handleCostBlur(item, defId)}
                            onKeyDown={(e) => handleCostKeyDown(item, defId, e)}
                            placeholder="—"
                            className="w-20 text-xs text-right border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                        />
                    </div>
                    {saving === true && <div className="text-[10px] text-gray-400 text-right">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500 text-right">{err}</div>}
                </td>
            );
        }
        if (col.id.startsWith("field:")) {
            const defId = col.id.slice(6);
            const key = fieldKey(item.id, defId);
            const saving = fieldSaving[key];
            const err = saving && typeof saving === "object" ? saving.error : null;
            return (
                <td key={col.id} className="px-3 py-2">
                    <input
                        type="text"
                        value={getFieldValue(item, defId)}
                        onChange={(e) => handleFieldChange(item, defId, e.target.value)}
                        onBlur={() => handleFieldBlur(item, defId)}
                        placeholder="—"
                        className="w-28 text-xs border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                    />
                    {saving === true && <div className="text-[10px] text-gray-400">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500">{err}</div>}
                </td>
            );
        }
        if (col.id === "total_fees") {
            const saving = feeSaving[item.id];
            const err = saving && typeof saving === "object" ? saving.error : null;
            const pending = feePendingConfirm[item.id];
            const breakdown = item.fee_breakdown || {};
            const hasBreakdown = Object.keys(breakdown).some((k) => breakdown[k]);

            return (
                <td key={col.id} className="px-3 py-2 text-right relative group" style={{ overflow: "visible" }}>
                    <div className="flex items-center justify-end gap-1">
                        <span className="text-gray-400 text-xs">$</span>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={getFeeDisplayValue(item)}
                            onChange={(e) => handleFeeChange(item, e.target.value)}
                            onBlur={() => handleFeeBlur(item)}
                            onKeyDown={(e) => handleFeeKeyDown(item, e)}
                            placeholder="—"
                            className="w-16 text-xs text-right border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                        />
                        {item.source && (
                            <span className="text-[9px] text-gray-300 uppercase">
                                {item.source === "batch" ? "auto" : item.source === "label" ? "label" : "manual"}
                            </span>
                        )}
                    </div>
                    {saving === true && <div className="text-[10px] text-gray-400 text-right">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500 text-right">{err}</div>}

                    {(() => {
                        const d = dimsById[item.id];
                        if (!d) {
                            return (
                                <button onClick={() => loadDims(item)}
                                    className="block ml-auto mt-0.5 text-[10px] text-gray-400 hover:text-teal-600 underline decoration-dotted whitespace-nowrap">
                                    dims & weight
                                </button>
                            );
                        }
                        if (d.loading) return <div className="text-[10px] text-gray-400 text-right mt-0.5">Loading...</div>;
                        if (d.error) return <div className="text-[10px] text-red-400 text-right mt-0.5">{d.error}</div>;
                        const dm = d.dimensions || {};
                        return (
                            <div className="text-[10px] text-gray-500 text-right mt-0.5 leading-tight">
                                <div>{dm.length != null ? `${dm.length} × ${dm.width} × ${dm.height} in` : "No dims"}</div>
                                <div>{d.weight != null ? `${d.weight} lb` : "No weight"}</div>
                            </div>
                        );
                    })()}

                    {hasBreakdown && (
                        <div className="hidden group-hover:block absolute z-[9999999] top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-1 w-64 text-left">
                            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown</p>
                            <div className="space-y-1 text-xs">
                                {Object.entries(FEE_LABELS).map(([key, label]) => (
                                    breakdown[key] ? (
                                        <div key={key} className="flex justify-between">
                                            <span className="text-gray-500">
                                                {label}
                                                {key === "referral_fee" && item.price ? (
                                                    <span className="text-gray-400"> ({snapReferralFeePercent(breakdown[key], item.price)}%)</span>
                                                ) : null}
                                            </span>
                                            <span className="font-semibold text-gray-700">${parseFloat(breakdown[key]).toFixed(2)}</span>
                                        </div>
                                    ) : null
                                ))}
                                {breakdown.manual != null && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Manual Override</span>
                                        <span className="font-semibold text-gray-700">${parseFloat(breakdown.manual).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-1 border-t border-gray-100">
                                    <span className="text-gray-600 font-bold">Total</span>
                                    <span className="font-bold text-red-600">${(item.total_fees || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {pending !== undefined && (
                        <div className="absolute z-[9999999] top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2.5 w-48 text-left">
                            <p className="text-[11px] text-gray-600 mb-2">
                                Override fees to <span className="font-bold">${pending}</span>? This replaces the calculated breakdown.
                            </p>
                            <div className="flex gap-1.5">
                                <button onClick={() => commitFee(item, pending)}
                                    className="flex-1 py-1 bg-teal-600 text-white rounded-lg text-[11px] font-bold hover:bg-teal-700 transition">
                                    OK
                                </button>
                                <button onClick={() => cancelFeeConfirm(item)}
                                    className="flex-1 py-1 border border-gray-200 text-gray-500 rounded-lg text-[11px] font-semibold hover:bg-gray-50 transition">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </td>
            );
        }
        if (col.id === "profit_roi") {
            const profit = item.estimated_profit;
            const roi = item.estimated_roi;
            return (
                <td key={col.id} className="px-3 py-2 text-right">
                    {profit != null ? (
                        <div className="flex flex-col items-end">
                            <span className={`font-bold text-xs ${profit >= 0 ? "text-green-600" : "text-red-600"}`}>${profit.toFixed(2)}</span>
                            {roi != null && (
                                <span className={`text-[10px] font-semibold ${roi >= 0 ? "text-purple-600" : "text-red-500"}`}>{roi.toFixed(1)}% ROI</span>
                            )}
                        </div>
                    ) : (
                        <span className="text-gray-300 text-xs">—</span>
                    )}
                </td>
            );
        }
        if (col.id === "price_incentive") {
            const incentives = incentivesBySku[item.sku] || [];
            if (incentives.length === 0) {
                return <td key={col.id} className="px-3 py-2"><span className="text-gray-300 text-xs">—</span></td>;
            }
            const hasEligible = incentives.some((i) => i.query_status === "ELIGIBLE");
            const hasEnrolled = incentives.some((i) => i.query_status === "ENROLLED");
            // Show the best (lowest) target price among all incentives for
            // this sku — that's the price Walmart wants the seller to hit.
            const targetPrices = incentives.map((i) => i.target_price).filter((p) => p != null);
            const bestTargetPrice = targetPrices.length > 0 ? Math.min(...targetPrices) : null;
            return (
                <td key={col.id} className="px-3 py-2">
                    <button onClick={() => setIncentiveModalItem(item)} className="flex flex-col items-start gap-1 text-left" title="Click for full incentive details">
                        {bestTargetPrice != null && (
                            <span className="text-xs font-semibold text-gray-700 underline">Target: {money(bestTargetPrice)}</span>
                        )}
                        {bestTargetPrice != null && item.estimated_profit != null && (() => {
                            const p = bestTargetPrice - (item.total_cost_basis || 0) - (item.total_fees || 0);
                            return <span className={`text-[10px] font-semibold ${p >= 0 ? "text-green-600" : "text-red-500"}`}>Profit @ target: ${p.toFixed(2)}</span>;
                        })()}
                        <div className="flex flex-wrap gap-1">
                            {hasEligible && (
                                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">
                                    <CheckCircle2 size={11} /> Eligible
                                </span>
                            )}
                            {hasEnrolled && (
                                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">
                                    <HelpCircle size={11} /> Enrolled
                                </span>
                            )}
                        </div>
                    </button>
                </td>
            );
        }
        if (col.id === "flags") {
            const isOpen = openFlagsFor === item.id;
            const visibleFlags = (item.flags || []).filter(
                (f) => flagOverride[flagKey(item.id, f.flag_definition_id)] !== false
            );
            const addedFlags = flagDefinitions.filter(
                (fd) =>
                    flagOverride[flagKey(item.id, fd.id)] === true &&
                    !(item.flags || []).some((f) => f.flag_definition_id === fd.id)
            );

            return (
                <td key={col.id} className="px-3 py-2 relative">
                    <div className="flex flex-wrap gap-1 max-w-[180px] items-center">
                        {visibleFlags.map((f) => (
                            <span key={f.flag_definition_id} style={{ backgroundColor: `${f.color}22`, color: f.color }}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full">{f.name}</span>
                        ))}
                        {addedFlags.map((fd) => (
                            <span key={fd.id} style={{ backgroundColor: `${fd.color}22`, color: fd.color }}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full">{fd.name}</span>
                        ))}
                        <button
                            onClick={() => setOpenFlagsFor(isOpen ? null : item.id)}
                            className="text-[10px] text-gray-400 hover:text-teal-600 border border-dashed border-gray-300 rounded-full px-2 py-0.5"
                        >
                            + Edit
                        </button>
                    </div>

                    {isOpen && (
                        <div className="absolute z-10 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 w-48">
                            {flagDefinitions.length === 0 && (
                                <div className="text-xs text-gray-400 px-1 py-1">No flags defined yet.</div>
                            )}
                            {flagDefinitions.map((fd) => {
                                const active = isFlagActive(item, fd.id);
                                const key = flagKey(item.id, fd.id);
                                const saving = flagSaving[key];
                                return (
                                    <button
                                        key={fd.id}
                                        onClick={() => toggleItemFlag(item, fd)}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left"
                                    >
                                        {active ? <CheckSquare size={14} style={{ color: fd.color }} /> : <Square size={14} className="text-gray-300" />}
                                        <span className="text-xs text-gray-700 flex-1">{fd.name}</span>
                                        {saving === true && <span className="text-[9px] text-gray-400">...</span>}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setOpenFlagsFor(null)}
                                className="w-full text-center text-[10px] text-gray-400 hover:text-gray-600 mt-1 pt-1 border-t border-gray-100"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </td>
            );
        }
        if (col.id === "status") {
            const reasons = item.unpublished_reasons || [];
            return (
                <td key={col.id} className="px-3 py-2">
                    <span
                        title={reasons.length ? reasons.join("\n") : undefined}
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.published_status === "PUBLISHED" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600 cursor-help"}`}
                    >
                        {item.published_status || "—"}
                        {reasons.length > 0 && ` (${reasons.length})`}
                    </span>
                </td>
            );
        }
        if (col.id === "notes") {
            const value = getNotesValue(item);
            const timestamp = getNotesTimestamp(item);
            const saving = notesSaving[item.id];
            const saveError = saving && typeof saving === "object" ? saving.error : null;
            return (
                <td key={col.id} className="px-3 py-2 min-w-[200px]">
                    <textarea
                        value={value}
                        onChange={(e) => handleNotesChange(item, e.target.value)}
                        onBlur={() => handleNotesBlur(item)}
                        placeholder="Add a note..."
                        rows={1}
                        className="w-full text-xs border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-2 py-1 resize-y focus:outline-none"
                    />
                    <div className="text-[10px] text-gray-400 mt-0.5 px-2">
                        {saving === true ? "Saving..." : saveError ? <span className="text-red-500">{saveError}</span> : timestamp ? `Saved ${formatNotesTimestamp(timestamp)}` : ""}
                    </div>
                </td>
            );
        }
        if (col.id === "supplier") {
            const key = metaKey(item.id, "supplier");
            const saving = metaSaving[key];
            const err = saving && typeof saving === "object" ? saving.error : null;
            return (
                <td key={col.id} className="px-3 py-2">
                    <input
                        type="text"
                        value={getMetaValue(item, "supplier")}
                        onChange={(e) => handleMetaChange(item, "supplier", e.target.value)}
                        onBlur={() => handleMetaBlur(item, "supplier")}
                        placeholder="—"
                        className="w-28 text-xs border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                    />
                    {saving === true && <div className="text-[10px] text-gray-400">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500">{err}</div>}
                </td>
            );
        }
        if (col.id === "expiration") {
            const key = metaKey(item.id, "expiration_date");
            const saving = metaSaving[key];
            const err = saving && typeof saving === "object" ? saving.error : null;
            return (
                <td key={col.id} className="px-3 py-2">
                    <input
                        type="date"
                        value={getMetaValue(item, "expiration_date")}
                        onChange={(e) => handleMetaChange(item, "expiration_date", e.target.value)}
                        onBlur={() => handleMetaBlur(item, "expiration_date")}
                        className="text-xs border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                    />
                    {saving === true && <div className="text-[10px] text-gray-400">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500">{err}</div>}
                </td>
            );
        }
        if (col.id === "purchase_date") {
            const key = metaKey(item.id, "purchase_date");
            const saving = metaSaving[key];
            const err = saving && typeof saving === "object" ? saving.error : null;
            return (
                <td key={col.id} className="px-3 py-2">
                    <input
                        type="date"
                        value={getMetaValue(item, "purchase_date")}
                        onChange={(e) => handleMetaChange(item, "purchase_date", e.target.value)}
                        onBlur={() => handleMetaBlur(item, "purchase_date")}
                        className="text-xs border border-transparent hover:border-gray-200 focus:border-teal-400 rounded-lg px-1.5 py-1 focus:outline-none"
                    />
                    {saving === true && <div className="text-[10px] text-gray-400">Saving...</div>}
                    {err && <div className="text-[10px] text-red-500">{err}</div>}
                </td>
            );
        }
        if (col.id === "date_added") {
            return (
                <td key={col.id} className="px-3 py-2 text-xs text-gray-500">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                </td>
            );
        }
        return <td key={col.id} className="px-3 py-2" />;
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <style>{`
                .catalog-resizable-table td {
                    overflow: hidden;
                    vertical-align: top;
                }
                .catalog-resizable-table td.locked-cell:hover { z-index: 29 !important; }
                .catalog-resizable-table td > * {
                    max-width: 100%;
                }
                .catalog-resizable-table td .truncate-cell {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .catalog-resizable-table td .clamp-cell {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
            <div
                ref={scrollContainerRef}
                onMouseMove={handleTableMouseMove}
                onMouseLeave={handleTableMouseLeave}
                className="overflow-x-auto overflow-y-auto h-[75vh]"
            >
                <table className="w-full text-sm catalog-resizable-table" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                        <col style={{ width: 44 }} />
                        {orderedColumns.map((col) => (
                            <col key={col.id} style={{ width: getColumnWidth(col.id) }} />
                        ))}
                    </colgroup>
                    <thead className="sticky top-0 z-30 bg-white">
                        <tr className="bg-gray-50 text-gray-500 text-xs uppercase select-none">
                            <th className="px-3 py-3 text-left relative sticky left-0 z-40 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    <button onClick={toggleSelectAll}>
                                        {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                                    </button>
                                    <button
                                        onClick={() => setShowColumnPicker((v) => !v)}
                                        title="Show/hide columns"
                                        className="text-gray-400 hover:text-teal-600 transition relative"
                                    >
                                        <Settings size={14} />
                                        {hiddenColumns.size > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 bg-teal-500 text-white rounded-full w-3.5 h-3.5 text-[8px] font-bold flex items-center justify-center">
                                                {hiddenColumns.size}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                {showColumnPicker && (
                                    <div ref={columnPickerRef}
                                        className="absolute z-30 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl p-2 w-56 max-h-80 overflow-y-auto normal-case">
                                        <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-gray-100">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase">Show / Hide Columns</span>
                                            <div className="flex items-center gap-2">
                                                <button onClick={resetColumnWidths} className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold">
                                                    Reset Widths
                                                </button>
                                                {hiddenColumns.size > 0 && (
                                                    <button onClick={() => setHiddenColumns(new Set())} className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold">
                                                        Reset
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {columns.map((col) => {
                                            const hidden = hiddenColumns.has(col.id);
                                            return (
                                                <button
                                                    key={col.id}
                                                    onClick={() => toggleColumnVisibility(col.id)}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left"
                                                >
                                                    {hidden ? <EyeOff size={13} className="text-gray-300 flex-shrink-0" /> : <Eye size={13} className="text-teal-600 flex-shrink-0" />}
                                                    <span className={`text-xs flex-1 truncate normal-case font-normal ${hidden ? "text-gray-400" : "text-gray-700"}`}>{col.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </th>
                            {orderedColumns.map((col) => {
                                const isLocked = lockedColumns.has(col.id);
                                return (
                                    <th
                                        key={col.id}
                                        draggable
                                        onDragStart={() => handleDragStart(col.id)}
                                        onDragOver={handleDragOver}
                                        onDrop={() => handleDrop(col.id)}
                                        onClick={() => col.sortable && onSortToggle(col.sortKey)}
                                        className={`relative px-3 py-3 text-left whitespace-nowrap overflow-hidden ${col.sortable ? "cursor-pointer hover:text-gray-700" : "cursor-grab"} ${dragId === col.id ? "opacity-40" : ""} ${isLocked ? "bg-gray-50" : ""}`}
                                        style={isLocked ? { position: "sticky", left: stickyLeftByColId[col.id], zIndex: 35 } : undefined}
                                        title={col.tooltip ? col.tooltip : "Drag to reorder"}
                                    >
                                        <span className="flex items-center gap-1 truncate">
                                            <GripVertical size={11} className="text-gray-300 flex-shrink-0" />
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleColumnLock(col.id); }}
                                                title={isLocked ? "Unlock column" : "Lock column (pin while scrolling)"}
                                                className="flex-shrink-0 text-gray-300 hover:text-teal-600 transition"
                                            >
                                                {isLocked ? <Lock size={11} className="text-teal-600" /> : <Unlock size={11} />}
                                            </button>
                                            <span className="truncate">{col.label}</span>
                                            {sortIcon(col)}
                                        </span>
                                        <span
                                            onMouseDown={(e) => startResize(e, col.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="absolute top-0 right-0 h-full w-2 cursor-col-resize hover:bg-teal-200"
                                            title="Drag to resize this column"
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.id} className="border-t border-gray-50 hover:bg-gray-50/60 transition group">
                                <td className="px-3 py-2 sticky left-0 z-20 bg-white group-hover:bg-gray-50">
                                    <button onClick={() => toggleSelect(item.id)}>
                                        {selectedIds.includes(item.id) ? <CheckSquare size={16} className="text-teal-600" /> : <Square size={16} className="text-gray-300" />}
                                    </button>
                                </td>
                                {orderedColumns.map((col) => {
                                    const cell = renderCell(col, item);
                                    if (!lockedColumns.has(col.id)) return cell;
                                    return React.cloneElement(cell, {
                                        style: {
                                            ...(cell.props.style || {}),
                                            position: "sticky",
                                            left: stickyLeftByColId[col.id],
                                            zIndex: 20,
                                        },
                                        className: `${cell.props.className || ""} locked-cell bg-white group-hover:bg-gray-50`,
                                    });
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {items.length === 0 && (
                <div className="text-center py-16 text-gray-400 text-sm">No products match these filters yet.</div>
            )}

            {selectedIds.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-gray-900 text-white px-4 py-3 flex items-center gap-4 justify-between shadow-2xl z-40">
                    <span className="text-sm">{selectedIds.length} selected</span>
                    <div className="flex gap-2">
                        <button onClick={onEditCosts} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition">
                            <DollarSign size={13} /> Set Cost
                        </button>
                        <button onClick={onEditFlags} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition">
                            <Tag size={13} /> Set Flags
                        </button>
                        <button onClick={onEditFields} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition">
                            <Tag size={13} /> Set Custom Field
                        </button>
                        <button onClick={onEditPrice} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition">
                            <Tag size={13} /> Set Price
                        </button>
                        <button onClick={onEditInventory} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition">
                            <Package size={13} /> Set SF Inventory
                        </button>
                        <button onClick={openPrintQtyModal} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-xs font-bold transition">
                            <Printer size={13} /> Print Labels ({selectedIds.length})
                        </button>
                        <button
                            onClick={() => setBulkRetireConfirm(true)}
                            disabled={selectedIds.every((id) => items.find((it) => it.id === id)?.lifecycle_status === "Retired")}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-xs font-bold transition disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <XCircle size={13} /> Retire Selected ({selectedIds.filter((id) => items.find((it) => it.id === id)?.lifecycle_status !== "Retired").length})
                        </button>
                        <button onClick={exportSelectedToCsv} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg text-xs font-bold transition">
                            📄 Start Batch with Selected (CSV)
                        </button>
                    </div>
                </div>
            )}

            {hoverPreview && (
                <div
                    className="fixed z-[999] bg-white border border-gray-200 rounded-xl shadow-2xl p-2 pointer-events-none"
                    style={{ top: hoverPreview.top, left: hoverPreview.left, transform: "translateY(-25%)" }}
                >
                    <img src={hoverPreview.url} alt="" className="w-56 h-56 object-contain" />
                </div>
            )}

            {titleTooltip && (
                <div
                    className="fixed z-[999] bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-2xl pointer-events-none max-w-xs leading-snug"
                    style={{ top: titleTooltip.top, left: titleTooltip.left }}
                >
                    {titleTooltip.text}
                </div>
            )}

            {retireConfirmItem && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => !retiring && setRetireConfirmItem(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3 mb-4">
                            <XCircle size={24} className="text-red-500 flex-shrink-0" />
                            <div>
                                <h3 className="font-bold text-gray-800 mb-1">Retire this item?</h3>
                                <p className="text-xs text-gray-500">
                                    This submits <span className="font-mono font-semibold">{retireConfirmItem.sku}</span> for
                                    permanent retirement from Walmart's catalog. Walmart says this can take up to 48 hours
                                    to take effect, and cannot be easily undone.
                                </p>
                            </div>
                        </div>
                        {retireError && <p className="text-xs text-red-500 mb-3">{retireError}</p>}
                        <div className="flex gap-2">
                            <button onClick={() => setRetireConfirmItem(null)} disabled={retiring}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-500 rounded-xl text-sm font-bold hover:bg-gray-50 transition disabled:opacity-50">
                                Cancel
                            </button>
                            <button onClick={handleRetireConfirmed} disabled={retiring}
                                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition disabled:opacity-50">
                                {retiring ? "Retiring..." : "Yes, Retire"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {retireResultItem && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setRetireResultItem(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3 mb-4">
                            <XCircle size={24} className="text-gray-400 flex-shrink-0" />
                            <div>
                                <h3 className="font-bold text-gray-800 mb-1">Retirement Submitted</h3>
                                <p className="text-xs text-gray-500">
                                    <span className="font-mono font-semibold">{retireResultItem.sku}</span> was submitted
                                    for retirement. Walmart says this can take up to 48 hours to take effect.
                                </p>
                            </div>
                        </div>

                        {retireResultItem.feedId ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4">
                                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Feed ID</p>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-mono text-xs text-gray-700 break-all">{retireResultItem.feedId}</span>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(retireResultItem.feedId);
                                            setFeedIdCopied(true);
                                            setTimeout(() => setFeedIdCopied(false), 1500);
                                        }}
                                        className="flex-shrink-0 text-teal-600 hover:text-teal-700"
                                        title="Copy Feed ID"
                                    >
                                        {feedIdCopied ? <Check size={15} /> : <Copy size={15} />}
                                    </button>
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1.5">
                                    Use this to check the retirement's status in Walmart Seller Center's Feed history.
                                </p>
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400 mb-4">Walmart didn't return a feed ID for this request.</p>
                        )}

                        <button onClick={() => setRetireResultItem(null)}
                            className="w-full py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-700 transition">
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {bulkRetireConfirm && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setBulkRetireConfirm(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3 mb-4">
                            <XCircle size={24} className="text-red-500 flex-shrink-0" />
                            <div>
                                <h3 className="font-bold text-gray-800 mb-1">
                                    Retire {selectedIds.filter((id) => items.find((it) => it.id === id)?.lifecycle_status !== "Retired").length} item(s)?
                                </h3>
                                <p className="text-xs text-gray-500">
                                    Each item will be submitted for permanent retirement from Walmart's catalog, one at
                                    a time. Walmart says this can take up to 48 hours to take effect per item, and it
                                    cannot be easily undone. Already-retired items in your selection will be skipped.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setBulkRetireConfirm(false)}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-500 rounded-xl text-sm font-bold hover:bg-gray-50 transition">
                                Cancel
                            </button>
                            <button onClick={handleBulkRetireConfirmed}
                                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition">
                                Yes, Retire All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {bulkRetiring && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <h3 className="font-bold text-gray-800 mb-3">Retiring items...</h3>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                            <div
                                className="h-full bg-red-500 transition-all"
                                style={{ width: `${bulkRetireProgress.total ? (bulkRetireProgress.done / bulkRetireProgress.total) * 100 : 0}%` }}
                            />
                        </div>
                        <p className="text-xs text-gray-500">{bulkRetireProgress.done} of {bulkRetireProgress.total} processed</p>
                    </div>
                </div>
            )}

            {bulkRetireResults && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setBulkRetireResults(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-bold text-gray-800 mb-3">
                            Retirement Submitted — {bulkRetireResults.filter((r) => !r.error).length}/{bulkRetireResults.length} succeeded
                        </h3>
                        <div className="space-y-2 mb-4">
                            {bulkRetireResults.map((r) => (
                                <div key={r.sku} className={`text-xs rounded-lg px-3 py-2 border ${r.error ? "bg-red-50 border-red-100 text-red-600" : "bg-gray-50 border-gray-100 text-gray-600"}`}>
                                    <span className="font-mono font-semibold">{r.sku}</span>
                                    {r.error ? ` — ${r.error}` : r.feedId ? ` — Feed ID: ${r.feedId}` : " — submitted"}
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setBulkRetireResults(null)}
                            className="w-full py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-700 transition">
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {showExportInstructions && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setShowExportInstructions(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-bold text-gray-800 mb-2">CSV Downloaded ✓</h3>
                        <p className="text-sm text-gray-500 mb-4">To start a batch with these items:</p>
                        <ol className="text-sm text-gray-600 space-y-2 mb-5 list-decimal list-inside">
                            <li>Go to <span className="font-semibold">Batches</span> and start a new batch</li>
                            <li>On the "Find a Product" screen, click <span className="font-semibold">Import from CSV</span></li>
                            <li>Upload the file you just downloaded</li>
                            <li>Pick <span className="font-semibold">SKU</span> (or UPC/GTIN) as the identifier column</li>
                            <li>Click Import — each item will be searched and added one at a time</li>
                        </ol>
                        <button onClick={() => setShowExportInstructions(false)}
                            className="w-full py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-700 transition">
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {replensModalItem && (
                <ReplensModal item={replensModalItem} onClose={() => setReplensModalItem(null)} />
            )}

            {incentiveModalItem && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setIncentiveModalItem(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-gray-800">Price Incentives — {incentiveModalItem.sku}</h3>
                            <button onClick={() => setIncentiveModalItem(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                        </div>
                        {(incentivesBySku[incentiveModalItem.sku] || []).map((inc, idx) => (
                            <div key={inc.incentive_id || idx} className="border border-gray-100 rounded-xl p-3 text-sm space-y-2 mb-3">
                                <div className="flex justify-between items-center">
                                    <span className="font-bold text-gray-700">{inc.incentive_type || "Incentive"}</span>
                                    <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${inc.query_status === "ENROLLED" ? "bg-orange-50 text-orange-600" : "bg-green-50 text-green-600"}`}>
                                        {inc.query_status === "ENROLLED" ? <HelpCircle size={11} /> : <CheckCircle2 size={11} />}
                                        {inc.query_status}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                                    <div>Current Price: <span className="font-semibold">{money(inc.current_price)}</span></div>
                                    <div>Target Price: <span className="font-semibold">{money(inc.target_price)}</span></div>
                                    <div>Shipping Price: <span className="font-semibold">{money(inc.shipping_price)}</span></div>
                                    <div>Inventory Count: <span className="font-semibold">{inc.inventory_count ?? "—"}</span></div>
                                    <div>Raw Status: <span className="font-semibold">{inc.incentive_status || "—"}</span></div>
                                    <div>Base Referral Fee: <span className="font-semibold">{inc.base_referral_fee != null ? `${inc.base_referral_fee}%` : "—"}</span></div>
                                    <div>Reduced Referral Fee: <span className="font-semibold">{inc.reduced_referral_fee != null ? `${inc.reduced_referral_fee}%` : "—"}</span></div>
                                    <div>Enrollment Type: <span className="font-semibold">{inc.enrollment_type || "—"}</span></div>
                                    <div>Enrollment Date: <span className="font-semibold">{formatEpochMs(inc.enrollment_date)}</span></div>
                                    <div>Start Date: <span className="font-semibold">{formatEpochMs(inc.start_date)}</span></div>
                                    <div>Expiration Date: <span className="font-semibold">{formatEpochMs(inc.expiration_date)}</span></div>
                                    <div className="col-span-2">Incentive Id: <span className="font-mono font-semibold">{inc.incentive_id || "—"}</span></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {changeSkuInfoItem && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setChangeSkuInfoItem(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-bold text-gray-800 mb-2">How to change a SKU</h3>
                        <p className="text-s text-gray-500 mb-4">
                            To change{" "}
                            <span className="font-mono font-semibold">{changeSkuInfoItem.sku}</span>, add it to a new batch
                            using the same UPC/GTIN, set the new SKU there, and submit - this will update the existing
                            listing with the new SKU!
                        </p>
                        <button onClick={() => setChangeSkuInfoItem(null)}
                            className="w-full py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-700 transition">
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {showPrintQtyModal && (
                <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={() => setShowPrintQtyModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-teal-500 to-blue-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-white font-bold text-sm flex items-center gap-2"><Printer size={16} /> Print Labels</h3>
                            <button onClick={() => setShowPrintQtyModal(false)} className="text-white/70 hover:text-white"><X size={18} /></button>
                        </div>
                        <div className="p-4 overflow-y-auto space-y-2">
                            {items.filter((it) => selectedIds.includes(it.id)).map((it) => (
                                <div key={it.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-2.5">
                                    <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-800 truncate">{it.title || it.sku}</p>
                                        <p className="text-[10px] text-gray-400 font-mono">{it.upc || it.gtin || "No barcode on file"}</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <span className="text-[10px] text-gray-400">Qty</span>
                                        <input
                                            type="number" min="1"
                                            value={printQuantities[it.id] ?? "1"}
                                            onChange={(e) => setPrintQuantities((prev) => ({ ...prev, [it.id]: e.target.value }))}
                                            className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-500"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-gray-100 flex-shrink-0">
                            <button onClick={handleConfirmBulkPrint}
                                className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition flex items-center justify-center gap-2 text-sm">
                                <Printer size={16} /> Print {selectedIds.length} Label{selectedIds.length === 1 ? "" : "s"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CatalogTable;